import { forwardRef } from '@chakra-ui/react';
import { Props, useChakraSelectProps } from 'chakra-react-select';
import { Ref, useState } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { GooglePlacesAutocompleteHandle } from 'react-google-places-autocomplete/build/types';

export type CityState = {
    city: string;
    state: string;
};

export interface SelectCityStateProps<CityState>
    extends Omit<Props, 'onChange' | 'value' | 'size' | 'variant'> {
    value?: CityState;
    onChange?: (value?: CityState) => void;
}

interface LengthOffset {
    length: number;
    offset: number;
}

interface AutocompleteSelection {
    label?: string;
    value?: {
        description?: string;
        matches_substrings?: LengthOffset[];
        place_id?: string;
        reference?: string;
        structured_formatting?: {
            main_text?: string;
            main_text_matched_substrings?: LengthOffset[];
            secondary_text?: string;
        };
        terms?: LengthOffset[];
        types?: string[];
    };
}

const denormalize = (cs?: CityState): AutocompleteSelection | undefined => {
    if (!cs) return;
    return {
        label: `${cs?.city || ''}, ${cs?.state || ''}`,
        value: {},
    };
};

const normalize = (cs?: AutocompleteSelection): CityState | undefined => {
    const city = cs?.value?.structured_formatting?.main_text;
    const state = cs?.value?.structured_formatting?.secondary_text;
    if (city && state) {
        return {
            city,
            state,
        };
    }
};

const SelectCityStateInner = (
    { value, onChange, ...props }: SelectCityStateProps<CityState>,
    ref: Ref<GooglePlacesAutocompleteHandle>
) => {
    const [inputValue, setInputValue] = useState(denormalize(value));

    const onChangeValue = (value: unknown) => {
        if (!value) {
            return;
        }
        setInputValue(value);
        onChange?.(normalize(value));
    };

    const selectProps = useChakraSelectProps({
        ...props,
        noOptionsMessage: () => null,
        components: {
            IndicatorSeparator: () => null,
            LoadingIndicator: () => null,
            LoadingMessage: () => null,
        },
        value: inputValue,
        onChange: onChangeValue,
        chakraStyles: {
            menu: (provided) => ({ ...provided, zIndex: 100 }),
        },
    });

    return (
        <GooglePlacesAutocomplete
            apiKey={process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY}
            apiOptions={{ language: 'en', region: 'us' }}
            autocompletionRequest={{
                componentRestrictions: { country: ['us'] },
                types: ['(cities)'],
            }}
            // @ts-expect-error TODO: Fix typing
            selectProps={selectProps}
            ref={ref}
        />
    );
};

const SelectCityState = forwardRef(SelectCityStateInner);

export default SelectCityState;
